const TYRO_SDK_SRC =
  process.env.REACT_APP_TYRO_SDK_URL ||
  'https://iclientsimulator.test.tyro.com/iclient-with-ui-v1.js';
const TYRO_LOCAL_HEADLESS_FALLBACK_SRC = 'https://iclientsimulator.test.tyro.com/iclient-v1.js';
const TYRO_LOCAL_HEADLESS_SDK_SRC =
  process.env.REACT_APP_TYRO_SDK_HEADLESS_URL ||
  (TYRO_SDK_SRC.includes('/iclient-with-ui-v1.js')
    ? TYRO_SDK_SRC.replace('/iclient-with-ui-v1.js', '/iclient-v1.js')
    : TYRO_LOCAL_HEADLESS_FALLBACK_SRC);
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);

let tyroLoaderPromise = null;

const normalizeUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  try {
    return new URL(value, window.location.origin).toString();
  } catch (_) {
    return value.trim();
  }
};

const isLocalhostRuntime = () => {
  if (typeof window === 'undefined') return false;
  const hostname = String(window.location?.hostname || '').toLowerCase();
  return LOCAL_HOSTNAMES.has(hostname);
};

const resolveTyroSdkSrc = ({ preferHeadlessScript = false } = {}) => {
  // Keep production behavior unchanged; only localhost can switch to headless SDK.
  if (preferHeadlessScript && isLocalhostRuntime()) {
    return TYRO_LOCAL_HEADLESS_SDK_SRC;
  }
  return TYRO_SDK_SRC;
};

export const loadTyroSdk = (options = {}) => {
  const { preferHeadlessScript = false } = options;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Tyro SDK can only be loaded in a browser environment.'));
  }

  const desiredSrc = resolveTyroSdkSrc({ preferHeadlessScript });
  const existingScript = document.querySelector('script[data-tyro-sdk="true"]');
  const existingSrc = normalizeUrl(existingScript?.getAttribute('src') || existingScript?.src || '');
  const desiredNormalizedSrc = normalizeUrl(desiredSrc);
  const isExistingDesiredScript = Boolean(existingSrc && existingSrc === desiredNormalizedSrc);

  if (window.TYRO && (!preferHeadlessScript || !isLocalhostRuntime() || isExistingDesiredScript)) {
    return Promise.resolve(window.TYRO);
  }

  if (tyroLoaderPromise) {
    return tyroLoaderPromise;
  }

  tyroLoaderPromise = new Promise((resolve, reject) => {
    let existingScript = document.querySelector('script[data-tyro-sdk="true"]');
    const existingSrc = normalizeUrl(existingScript?.getAttribute('src') || existingScript?.src || '');
    const shouldReplaceExistingScript = Boolean(
      existingScript && existingSrc && existingSrc !== desiredNormalizedSrc
    );

    if (shouldReplaceExistingScript) {
      existingScript.remove();
      window.TYRO = undefined;
      existingScript = null;
    }

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.TYRO));
      existingScript.addEventListener('error', () =>
        reject(new Error('Failed to load Tyro SDK (existing script).'))
      );
      return;
    }

    const script = document.createElement('script');
    script.src = desiredSrc;
    script.async = true;
    script.dataset.tyroSdk = 'true';
    script.onload = () => resolve(window.TYRO);
    script.onerror = () => reject(new Error('Failed to load Tyro SDK.'));
    document.body.appendChild(script);
  }).finally(() => {
    tyroLoaderPromise = null;
  });

  return tyroLoaderPromise;
};

export const hasTyroSdkLoaded = () => Boolean(typeof window !== 'undefined' && window.TYRO);
